import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { useDispatch, useSelector } from 'react-redux';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import LinkIcon from '@mui/icons-material/Link';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import './style.css';
import { MODAL_TOGGLE } from '../../redux/actions/app';
import { useSnackbar } from 'notistack';
import { ID } from './config';
import NodeSelect from '../NodeSelect';
import { CONNECTION_ADD } from '../../redux/actions/board';

const CreateConnection = () => {

    const dispatch = useDispatch(),
    { enqueueSnackbar } = useSnackbar(),
    labels = useMemo( () => new LocalizedStrings( LOCALIZATIONS ), []),
    { locale, modal } = useSelector( state => state.app ),
    { currentNode, connections, nodes } = useSelector( state => state.board ),
    [ node, setNode ] = useState( null ),
    [ filters, setFilters ] = useState([]),
    open = Boolean( modal === ID ),
    disabled = !Boolean( node );

    /**
     * @name cancel
     * @function
     * @description Hides the modal
     */
    const cancel = useCallback(() => {
        dispatch({ type: MODAL_TOGGLE });
        setFilters([]);
        setNode( null );
    }, [ dispatch, setFilters, setNode ]);

    /**
     * @name connect
     * @function
     * @description Connects the selected nodes and closes the modal
     */
    const connect = () => {
        dispatch({ type: CONNECTION_ADD, payload: [ currentNode.id, node.id ] });
        cancel();
    }

    /**
     * @name onSelect
     * @function
     * @description Sets the node to connect the target node to
     * @param {HTMLElement} target  
     */
    const onSelect = e => {
        setNode( nodes[ e.target.value ] )
    }

    // filter available opens
    useEffect(() => {

        const ids = [ currentNode?.id ];

        currentNode?.connections?.forEach( c => {
            connections[ c ].forEach( id => {
                if( !ids.includes( id ) ) ids.push( id );
            });
        });

        if( ids.length > 0 ) setFilters( ids );

    }, [ currentNode, connections, setFilters ]);

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale );
    }, [ labels, locale ]);

    // let user know there's no more viable connections
    useEffect(() => {
        if( open && filters.length === Object.keys( nodes ).length ){
            enqueueSnackbar(
                labels.noMoreConnections,
                { variant: 'info' }
            );
            cancel();
        }
    }, [ cancel, enqueueSnackbar, open, filters, nodes, labels ]);

    return (
        <Dialog open={ open } onClose={ cancel }>
            <DialogTitle>
                { 
                    labels.formatString( labels.addConnection, currentNode?.name || '' ) 
                }
            </DialogTitle>
            <DialogContent>
                <Stack>
                    <NodeSelect id="node" label={ labels.connectTo } onChange={ onSelect } value={ node?.id || '' } filter={ filters } />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button aria-label={ labels.connect } disabled={ disabled } onClick={ connect } variant="contained" startIcon={ <LinkIcon /> }>
                    { labels.connect }
                </Button>
                <Button aria-label={ labels.cancel } variant="outlined" onClick={ cancel } startIcon={ <NotInterestedIcon /> }>
                    { labels.cancel }
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default CreateConnection;